import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Checkbox from "@/components/atomic/Checkbox";
import Label from "@/components/atomic/Label";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { BasePromptEditForm } from "@/components/ui/BasePromptEditForm";
import { routes } from "@/configs/router.config/routes";
import { useSubAgent } from "@/hooks/agent/useSubAgent";
import { useSubAgentMutations } from "@/hooks/agent/useSubAgentMutations";
import { useWorkspaces } from "@/hooks/useWorkspaces";

// 利用可能なツールのリスト（実際のツールリストに応じて調整）
const availableTools = [
  { value: "write_file", label: "ファイル作成・上書き" },
  { value: "read_file", label: "ファイル内容の読み取り（範囲指定可）" },
  { value: "edit_file", label: "ファイル内の特定テキストを置換" },
  { value: "search_file", label: "ファイル名パターン検索（glob対応）" },
  { value: "search_text", label: "複数ファイル内容の正規表現検索" },
  { value: "execute_command", label: "シェルコマンド実行（ブロッキング/非ブロッキング、tmuxセッション対応）" },
  { value: "check_command_output", label: "tmuxセッションの出力確認" },
  { value: "terminate_command", label: "実行中コマンドの終了" },
  { value: "list_commands", label: "実行中のtmuxセッション一覧" },
  { value: "WebSearchAgent", label: "ウェブ検索" },
  { value: "general_purpose_agent", label: "サブエージェントの動的作成・実行" },
  { value: "execute_sql", label: "SQLクエリを実行" },
  { value: "execute_sql_and_save_result_to_csv", label: "SQLクエリを実行して結果をファイル保存" },
];

const SubAgentFormPage: React.FC = () => {
  const { workspaceId, subAgentId } = useParams<{ workspaceId: string; subAgentId?: string; }>();
  const navigate = useNavigate();
  const { workspaces } = useWorkspaces();
  const [searchParams] = useSearchParams();

  const { subAgent } = useSubAgent(subAgentId || null);
  const { createSubAgent, updateSubAgent, isCreating, isUpdating } = useSubAgentMutations();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instruction, setInstruction] = useState("");
  const [tools, setTools] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("all");
  const [selectedUserScope, setSelectedUserScope] = useState<string>("all");
  const isEdit = !!subAgentId;

  const workspaceOptions = useMemo(
    () => [
      { value: "all", label: "All Workspaces" },
      ...(workspaces ? workspaces.map((ws) => ({ value: ws.id, label: ws.name })) : []),
    ],
    [workspaces],
  );

  const userScopeOptions = [
    { value: "all", label: "全ユーザー" },
    { value: "personal", label: "自分のみ" },
  ];

  useEffect(() => {
    if (subAgent) {
      setName(subAgent.name);
      setDescription(subAgent.description);
      setInstruction(subAgent.instruction);
      setTools(subAgent.tools);
      setEnabled(subAgent.enabled);
      setSelectedWorkspaceId(
        !subAgent.workspaceId || subAgent.workspaceId === "None" ? "all" : subAgent.workspaceId,
      );
      setSelectedUserScope(subAgent.userId ? "personal" : "all");
    }
  }, [subAgent]);

  const handleSubmit = async () => {
    if (!workspaceId) return;

    const requestData = {
      name,
      description,
      instruction,
      tools,
      scope_workspace: selectedWorkspaceId === "all" ? "all_workspace" : selectedWorkspaceId as "all" | "all_workspace",
      scope_user: selectedUserScope === "all" ? "all_user" : "self_only" as "all_user" | "self_only",
    };

    if (isEdit && subAgentId) {
      await updateSubAgent(subAgentId, {
        ...requestData,
        enabled,
      });
    } else {
      await createSubAgent(requestData);
    }

    // タブパラメータを保持して戻る
    const tab = searchParams.get("tab") || "subAgents";
    navigate(`${routes.agentManagement(workspaceId)}?tab=${tab}`);
  };

  const handleCancel = () => {
    if (!workspaceId) return;
    const tab = searchParams.get("tab") || "subAgents";
    navigate(`${routes.agentManagement(workspaceId)}?tab=${tab}`);
  };

  const toggleTool = (toolValue: string) => {
    setTools((prev) =>
      prev.includes(toolValue)
        ? prev.filter((t) => t !== toolValue)
        : [...prev, toolValue]
    );
  };

  if (!workspaceId) return null;

  return (
    <BasePromptEditForm
      title={isEdit ? "サブエージェントを編集" : "サブエージェントを作成"}
      name={name}
      setName={setName}
      description={description}
      setDescription={setDescription}
      instruction={instruction}
      setInstruction={setInstruction}
      enabled={enabled}
      setEnabled={setEnabled}
      selectedWorkspaceId={selectedWorkspaceId}
      setSelectedWorkspaceId={setSelectedWorkspaceId}
      selectedUserScope={selectedUserScope}
      setSelectedUserScope={setSelectedUserScope}
      workspaceOptions={workspaceOptions}
      userScopeOptions={userScopeOptions}
      isSaving={isCreating || isUpdating}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      namePlaceholder="Enter sub agent name"
      additionalFields={
        <div className="">
          <Label>Tools</Label>
          <ScrollArea className="mt-1 h-25 border rounded-md">
            <div className="space-y-2 p-4">
              {availableTools.map((tool) => (
                <div key={tool.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tool-${tool.value}`}
                    checked={tools.includes(tool.value)}
                    onCheckedChange={() => toggleTool(tool.value)}
                  />
                  <Label htmlFor={`tool-${tool.value}`} className="cursor-pointer">
                    {tool.value} - {tool.label}
                  </Label>
                </div>
              ))}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      }
    />
  );
};

export default SubAgentFormPage;
